"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useT } from "./LocaleProvider";

/**
 * Default-deny cookie consent banner with Consent Mode v2 wiring.
 *
 * - localStorage key `nysg-consent-v1`: "granted" | "denied"
 * - On mount: if no value stored → render fixed-bottom banner.
 *             If "granted" → silently emit consent grants (return-visitor restore).
 *             If "denied"  → silently stay denied (no banner, no grants).
 * - On Accept / Decline → persist choice + emit gtag/fbq consent update.
 *
 * Hydration-safe: returns null on server and until useEffect runs.
 *
 * Bilingual ES/EN inline copy per shop convention. NYSG palette via CSS vars.
 */

const STORAGE_KEY = "nysg-consent-v1";

type Choice = "granted" | "denied";

function applyConsent(choice: Choice): void {
  if (typeof window === "undefined") return;
  window.gtag?.("consent", "update", {
    analytics_storage: choice === "granted" ? "granted" : "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  window.fbq?.("consent", choice === "granted" ? "grant" : "revoke");
}

export function ConsentBanner() {
  const [visible, setVisible] = useState(false);
  const t = useT();

  useEffect(() => {
    let stored: string | null = null;
    try {
      stored = window.localStorage.getItem(STORAGE_KEY);
    } catch {
      // Private mode / disabled storage — treat as no choice, show banner.
      stored = null;
    }
    if (stored === "granted") {
      // Return visitor previously accepted — restore grants silently.
      applyConsent("granted");
      return;
    }
    if (stored === "denied") {
      // Defaults are already denied; nothing to emit. Stay quiet.
      return;
    }
    // No choice on file → render banner.
    setVisible(true);
  }, []);

  function choose(choice: Choice) {
    try {
      window.localStorage.setItem(STORAGE_KEY, choice);
    } catch {
      // Best effort — if storage fails, still emit the consent signal for
      // this session so the user's intent is honored at least once.
    }
    applyConsent(choice);
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label={t({
        en: "Cookie consent",
        es: "Consentimiento de cookies",
      })}
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 200,
        background: "var(--color-ink)",
        color: "var(--color-bone)",
        borderTop: "1px solid var(--color-foil)",
        maxHeight: "12rem",
      }}
    >
      <div
        style={{
          maxWidth: 1400,
          margin: "0 auto",
          padding: "16px 24px",
          display: "flex",
          flexWrap: "wrap",
          gap: 16,
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ flex: "1 1 320px", minWidth: 240 }}>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5 }}>
            {t({
              en: "We use cookies for analytics and to remember your bag.",
              es: "Usamos cookies para analítica y para recordar tu bolsa.",
            })}
          </p>
          <Link
            href="/privacy"
            style={{
              display: "inline-block",
              marginTop: 6,
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: "var(--color-foil)",
              textDecoration: "underline",
              textUnderlineOffset: 3,
            }}
          >
            {t({ en: "Privacy policy", es: "Política de privacidad" })}
          </Link>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => choose("denied")}
            style={{
              padding: "10px 18px",
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              background: "transparent",
              color: "var(--color-bone)",
              border: "1px solid var(--color-bone)",
              cursor: "pointer",
            }}
          >
            {t({ en: "Decline", es: "Rechazar" })}
          </button>
          <button
            type="button"
            onClick={() => choose("granted")}
            style={{
              padding: "10px 18px",
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              background: "var(--color-blood)",
              color: "var(--color-bone)",
              border: "1px solid var(--color-blood)",
              cursor: "pointer",
            }}
          >
            {t({ en: "Accept", es: "Aceptar" })}
          </button>
        </div>
      </div>
    </div>
  );
}
